import { useLayoutEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material';
import { TextField } from '@calefy-inc/informedMaterial';
import { useField, useFormState } from 'informed';
import { useQuery } from '@apollo/client';
import { GET_AVAILABLE_FONTS } from '../../../../../../queries/whiteLabelSettings';
import {
  WhiteLabelSectionDivider,
  WhiteLabelSectionRoot,
} from './whiteLabelSectionCommon';

export interface TypographySectionProps {
  /** From `whiteLabelTypographyHeadingLevels` — same order as Django `WhiteLabelTypography` heading FKs. */
  typographyHeadingLevels: readonly string[];
}

interface FontSelectProps {
  name: string;
  label: string;
  fonts: string[];
  /** When false, empty option is hidden and the first font is applied if the field is empty. */
  allowNone?: boolean;
}

/** MUI Select integrated with Informed form state */
function FontSelect({ name, label, fonts, allowNone = true }: FontSelectProps) {
  const { fieldState, fieldApi } = useField({ name });
  const raw = fieldState.value;
  const value = typeof raw === 'string' ? raw : '';

  // Depend on the first face name (primitive), not the `fonts` array — parent may rebuild the array each render.
  const firstFontOption = fonts.length > 0 ? fonts[0] : '';

  // Primary font must always be set — default to the first available face when loading or legacy empty.
  useLayoutEffect(() => {
    if (!allowNone && firstFontOption !== '' && value === '') {
      fieldApi.setValue(firstFontOption);
    }
  }, [allowNone, fieldApi, firstFontOption, value]);

  const selectValue = allowNone ? value : value || firstFontOption;

  return (
    <FormControl variant='standard' fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selectValue}
        onChange={(e) => fieldApi.setValue(e.target.value)}
        label={label}
      >
        {allowNone ? (
          <MenuItem value=''>
            <em>None (inherit)</em>
          </MenuItem>
        ) : null}
        {fonts.map((font) => (
          <MenuItem key={font} value={font}>
            {font}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/** Resolves preview `font-family` using backend `family` when the face is a known FontName. */
function fontStackForPreview(
  fontName: string,
  fontFamilyByName: ReadonlyMap<string, string>,
): string {
  if (fontName.length === 0) {
    return 'inherit';
  }
  const fromApi = fontFamilyByName.get(fontName);
  if (fromApi !== undefined && fromApi.length > 0) {
    return fromApi;
  }
  // Unknown or legacy face name: no generic fallback guessed on the frontend.
  return fontName;
}

/** Live preview of the primary font (CSS stack from `availableFontChoices.family`). */
function PrimaryFontPreviewBox({
  fontFamilyByName,
}: {
  fontFamilyByName: ReadonlyMap<string, string>;
}) {
  const { values } = useFormState();
  const raw = values.fontFamily;
  const primaryName = typeof raw === 'string' ? raw : '';
  const fontStack = fontStackForPreview(primaryName, fontFamilyByName);

  return (
    <PreviewPanel>
      <PreviewCaption variant='subtitle2'>Primary font preview</PreviewCaption>
      <PreviewSampleText variant='body1' $fontStack={fontStack}>
        This font will be used across your website.
      </PreviewSampleText>
    </PreviewPanel>
  );
}

/** MUI heading variant for preview line — unknown level keys fall back to h6. */
function muiVariantForHeadingLevel(
  level: string,
): 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' {
  const k = level.toLowerCase();
  if (k === 'h1') return 'h1';
  if (k === 'h2') return 'h2';
  if (k === 'h3') return 'h3';
  if (k === 'h4') return 'h4';
  if (k === 'h5') return 'h5';
  if (k === 'h6') return 'h6';
  return 'h6';
}

/** Live preview lines for each heading select (`typography_${level}`). */
function HeadingLevelsPreviewBox({
  typographyHeadingLevels,
  fontFamilyByName,
}: {
  typographyHeadingLevels: readonly string[];
  fontFamilyByName: ReadonlyMap<string, string>;
}) {
  const { values } = useFormState();
  const primaryRaw = values.fontFamily;
  const primaryName = typeof primaryRaw === 'string' ? primaryRaw : '';
  const primaryStack = fontStackForPreview(primaryName, fontFamilyByName);

  return (
    <PreviewPanel>
      <PreviewCaption variant='subtitle2'>Heading previews</PreviewCaption>
      <HeadingPreviewsList>
        {typographyHeadingLevels.map((level) => {
          const fieldKey = `typography_${level}`;
          const rawHeading = values[fieldKey];
          const headingName =
            typeof rawHeading === 'string' && rawHeading.length > 0
              ? rawHeading
              : '';
          const fontStack =
            headingName.length > 0
              ? fontStackForPreview(headingName, fontFamilyByName)
              : primaryStack;
          const label = level.toUpperCase();
          return (
            <HeadingPreviewSample
              key={level}
              variant={muiVariantForHeadingLevel(level)}
              $fontStack={fontStack}
            >
              {`${label} sample — The quick brown fox jumps over the lazy dog`}
            </HeadingPreviewSample>
          );
        })}
      </HeadingPreviewsList>
    </PreviewPanel>
  );
}

export function TypographySection({
  typographyHeadingLevels,
}: TypographySectionProps) {
  const { data, loading } = useQuery(GET_AVAILABLE_FONTS);
  const fontFamilyByName = new Map<string, string>();
  for (const row of data?.availableFontChoices ?? []) {
    if (row == null) {
      continue;
    }
    const n = row.name;
    const fam = row.family;
    if (typeof n === 'string' && n.length > 0 && typeof fam === 'string') {
      fontFamilyByName.set(n, fam);
    }
  }
  const fonts: string[] = [...fontFamilyByName.keys()];

  if (loading) {
    return <CircularProgress size={20} />;
  }

  return (
    <WhiteLabelSectionRoot>
      <Typography variant='h6' gutterBottom>
        Typography
      </Typography>
      <WhiteLabelSectionDivider />

      <PrimaryFieldsGrid>
        <FontSelect
          name='fontFamily'
          label='Primary Font'
          fonts={fonts}
          allowNone={false}
        />
        <TextField
          name='subtitleFontWeight'
          label='Subtitle Font Weight'
          variant='standard'
          fullWidth
          placeholder='600'
          type='number'
        />
      </PrimaryFieldsGrid>
      <PrimaryFontPreviewBox fontFamilyByName={fontFamilyByName} />

      {typographyHeadingLevels.length > 0 ? (
        <>
          <HeadingBlockTitle sx={{ marginTop: 2 }} variant='subtitle2'>
            Heading Fonts
          </HeadingBlockTitle>
          <HeadingFieldsGrid>
            {typographyHeadingLevels.map((level) => (
              <FontSelect
                key={level}
                name={`typography_${level}`}
                label={level.toUpperCase()}
                fonts={fonts}
              />
            ))}
          </HeadingFieldsGrid>
          <HeadingLevelsPreviewBox
            typographyHeadingLevels={typographyHeadingLevels}
            fontFamilyByName={fontFamilyByName}
          />
        </>
      ) : null}
    </WhiteLabelSectionRoot>
  );
}

// --- Section-specific layout ---

const PrimaryFieldsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  gridTemplateColumns: '1fr',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr 1fr',
  },
}));

const HeadingFieldsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(2, 1fr)',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}));

const HeadingBlockTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const PreviewPanel = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  // ~`sx` borderRadius: 2 (theme.shape is not always numeric in typings)
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.action.hover,
  border: '1px solid',
  borderColor: theme.palette.divider,
}));

const PreviewCaption = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  color: theme.palette.text.secondary,
  fontWeight: 500,
}));

/** `$fontStack`: transient — CSS `font-family` for preview only, not forwarded to DOM. */
const PreviewSampleText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$fontStack',
})<{ $fontStack: string }>(({ theme, $fontStack }) => ({
  fontFamily: $fontStack,
  color: theme.palette.text.primary,
}));

const HeadingPreviewsList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1),
}));

const HeadingPreviewSample = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$fontStack',
})<{ $fontStack: string }>(({ theme, $fontStack }) => ({
  fontFamily: $fontStack,
  color: theme.palette.text.primary,
  lineHeight: 1.25,
}));
